import { useNavigate } from 'react-router-dom';
import { BingoCardView } from '@/components/BingoCardView';
import { api } from '@/lib/api';
import { etb } from '@/lib/money';
import type { BingoCard, GameSummary } from '@/lib/types';

export interface GameResult {
    won: boolean;
    winnerName: string | null;
    payout: number | null;
    winningCartela: number | null;
    winningCard: BingoCard | null;
    drawn: Set<number>;
}

interface Props {
    result: GameResult;
    /** Stake of the round just finished — used to reopen that table’s cartela board. */
    stakeMinor?: number | null;
    onClose?: () => void;
}

export function GameResultModal({ result, stakeMinor = null, onClose }: Props) {
    const navigate = useNavigate();

    const playAgain = async (): Promise<void> => {
        onClose?.();
        try {
            if (stakeMinor !== null && stakeMinor !== undefined) {
                const res = await api.get<{ data: GameSummary[] }>('/games');
                const next = res.data.data.find(
                    (g) =>
                        (g.status === 'open' || g.status === 'active') &&
                        g.stake_amount === stakeMinor,
                );
                if (next) {
                    navigate(`/games/${next.id}/purchase`, { replace: true });
                    return;
                }
            }
        } catch {
            // fall through to lobby
        }
        navigate('/', { replace: true });
    };

    return (
        <div className="w-result-overlay" role="dialog" aria-modal="true">
            <div className={`w-result-card ${result.won ? 'won' : 'lost'}`}>
                <div className="w-result-banner">{result.won ? 'YOU WON' : 'YOU LOST'}</div>
                {!result.won && (
                    <p className="w-result-sub">
                        {result.winnerName ? `${result.winnerName} won the game` : 'Another player won this round'}
                    </p>
                )}
                {result.won && <p className="w-result-sub">Congratulations!</p>}

                {!result.won && result.winningCard && (
                    <>
                        <div className="w-result-section-title">🏆 Winning Cartela</div>
                        {result.winningCartela !== null && (
                            <div className="w-cartela-label">Cartela {result.winningCartela}</div>
                        )}
                        <div className="w-result-card-preview">
                            <BingoCardView card={result.winningCard} drawn={result.drawn} highlight />
                        </div>
                    </>
                )}

                <div className="w-result-section-title">🏆 {result.won ? 'Your prize' : 'Winner'}</div>
                <div className="w-result-prize-row">
                    <span>{result.won ? 'You' : (result.winnerName ?? 'Winner')}</span>
                    <strong>{result.payout !== null ? `${etb(result.payout)} ETB` : '—'}</strong>
                </div>

                <button type="button" className="w-play-again" onClick={() => void playAgain()}>
                    Play Again
                </button>
            </div>
        </div>
    );
}
